import { useState } from 'react';
import { api } from '../api';
import { useApp } from '../AppContext';

export default function Login({ onLogin }) {
  const { site, theme, toggleTheme } = useApp();
  const [tab, setTab]       = useState('password');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [pin, setPin]       = useState('');
  const [step, setStep]     = useState('credentials');
  const [totp, setTotp]     = useState('');
  const [error, setError]   = useState('');
  const [loading, setLoading] = useState(false);

  async function submitCredentials(pinOverride) {
    const p = pinOverride ?? pin;
    if (!username.trim()) { setError('Enter your username.'); return; }
    if (tab === 'password' && !password) { setError('Enter your password.'); return; }
    if (tab === 'pin' && p.length !== 4) { setError('Enter your 4-digit PIN.'); return; }
    setError(''); setLoading(true);
    const res = await api.post('/api/login', {
      username: username.trim(),
      ...(tab === 'password' ? { password } : { pin: p }),
    });
    setLoading(false);
    if (res?.data?.requires_2fa) { setStep('2fa'); return; }
    if (res?.ok && res.data?.success) { onLogin(); return; }
    setError(res?.data?.error || 'Invalid credentials.'); setPin('');
  }

  function pinPress(key) {
    if (key === 'clear') { setPin(''); return; }
    if (key === 'back')  { setPin(p => p.slice(0, -1)); return; }
    if (pin.length >= 4) return;
    const next = pin + key;
    setPin(next);
    if (next.length === 4) submitCredentials(next);
  }

  async function submit2FA() {
    if (!totp.trim()) { setError('Enter the 6-digit code.'); return; }
    setError(''); setLoading(true);
    const res = await api.post('/api/login', { username, totp_code: totp });
    setLoading(false);
    if (res?.ok && res.data?.success) { onLogin(); return; }
    setError(res?.data?.error || 'Invalid code.'); setTotp('');
  }

  const inputStyle = {
    width: '100%', padding: '10px 13px',
    border: '1px solid var(--input-border)',
    borderRadius: 'var(--radius)',
    background: 'var(--input-bg)', color: 'var(--fg)',
    fontSize: 14, fontFamily: 'var(--font)', outline: 'none',
  };

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, position: 'relative' }}>

      {/* Theme toggle */}
      <button onClick={toggleTheme} style={{ position: 'fixed', top: 16, right: 16, width: 36, height: 36, borderRadius: 'var(--radius)', border: '1px solid var(--border)', background: 'var(--bg2)', cursor: 'pointer', fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {theme === 'dark' ? '☀️' : '🌙'}
      </button>

      <div style={{ width: '100%', maxWidth: 380, animation: 'slideUp 0.2s ease' }}>

        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{ width: 48, height: 48, background: 'var(--accent)', borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px' }}>
            <span style={{ color: '#fff', fontSize: 18, fontWeight: 800, fontFamily: 'var(--font)', letterSpacing: '-0.04em' }}>SD</span>
          </div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--fg)', letterSpacing: '-0.02em', fontFamily: 'var(--font)', marginBottom: 4 }}>{site?.name || 'SwimDesk'}</h1>
          <p style={{ fontSize: 13, color: 'var(--fg3)', fontFamily: 'var(--font)' }}>
            {step === '2fa' ? 'Two-factor authentication' : 'Sign in to your account'}
          </p>
        </div>

        {/* Card */}
        <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-md)', overflow: 'hidden' }}>
          <div style={{ padding: '22px 24px' }}>

            {error && (
              <div style={{ padding: '9px 12px', background: 'var(--danger-dim)', border: '1px solid var(--danger)', borderRadius: 'var(--radius)', color: 'var(--danger)', fontSize: 13, fontWeight: 500, marginBottom: 16 }}>
                {error}
              </div>
            )}

            {step === 'credentials' && (
              <>
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--fg2)', marginBottom: 6 }}>Username</label>
                  <input style={inputStyle} type="text" value={username} onChange={e => setUsername(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && tab === 'password' && submitCredentials()}
                    onFocus={e => e.target.style.borderColor = 'var(--accent)'}
                    onBlur={e => e.target.style.borderColor = 'var(--input-border)'}
                    autoFocus autoComplete="username" />
                </div>

                {/* Tab toggle */}
                <div style={{ display: 'flex', marginBottom: 16, background: 'var(--bg)', borderRadius: 'var(--radius)', padding: 3, gap: 3 }}>
                  {['password', 'pin'].map(t => (
                    <button key={t} onClick={() => { setTab(t); setPin(''); setError(''); }} style={{
                      flex: 1, padding: '7px', border: 'none', cursor: 'pointer',
                      borderRadius: 'calc(var(--radius) - 1px)', fontFamily: 'var(--font)',
                      fontSize: 12, fontWeight: 600,
                      background: tab === t ? 'var(--card-bg)' : 'transparent',
                      color: tab === t ? 'var(--fg)' : 'var(--fg3)',
                      boxShadow: tab === t ? 'var(--shadow)' : 'none',
                    }}>
                      {t === 'password' ? 'Password' : 'PIN'}
                    </button>
                  ))}
                </div>

                {tab === 'password' && (
                  <>
                    <div style={{ marginBottom: 18 }}>
                      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--fg2)', marginBottom: 6 }}>Password</label>
                      <input style={inputStyle} type="password" value={password} onChange={e => setPassword(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && submitCredentials()}
                        onFocus={e => e.target.style.borderColor = 'var(--accent)'}
                        onBlur={e => e.target.style.borderColor = 'var(--input-border)'}
                        autoComplete="current-password" />
                    </div>
                    <button onClick={() => submitCredentials()} disabled={loading} style={{
                      width: '100%', padding: '10px', borderRadius: 'var(--radius)', border: 'none',
                      background: 'var(--accent)', color: '#fff', fontSize: 14, fontWeight: 600,
                      cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1,
                      fontFamily: 'var(--font)', boxShadow: '0 1px 3px rgba(37,99,235,0.3)',
                    }}>
                      {loading ? 'Signing in...' : 'Sign In'}
                    </button>
                  </>
                )}

                {tab === 'pin' && (
                  <>
                    <div style={{ textAlign: 'center', marginBottom: 16 }}>
                      <div style={{ display: 'flex', gap: 10, justifyContent: 'center', marginBottom: 14 }}>
                        {[0,1,2,3].map(i => (
                          <div key={i} style={{ width: 14, height: 14, borderRadius: '50%', background: i < pin.length ? 'var(--accent)' : 'var(--bg4)', transition: 'background 0.15s ease' }} />
                        ))}
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 8, maxWidth: 200, margin: '0 auto' }}>
                        {[1,2,3,4,5,6,7,8,9].map(n => (
                          <button key={n} onClick={() => pinPress(String(n))} style={{ padding: '14px', fontSize: 18, fontWeight: 600, border: '1px solid var(--border)', borderRadius: 'var(--radius)', background: 'var(--bg)', color: 'var(--fg)', cursor: 'pointer', fontFamily: 'var(--font)', transition: 'all 0.1s' }}
                            onMouseDown={e => e.currentTarget.style.background = 'var(--accent-dim)'}
                            onMouseUp={e => e.currentTarget.style.background = 'var(--bg)'}
                          >{n}</button>
                        ))}
                        <button onClick={() => pinPress('clear')} style={{ padding: '14px', fontSize: 11, fontWeight: 700, border: '1px solid var(--border)', borderRadius: 'var(--radius)', background: 'var(--bg)', color: 'var(--danger)', cursor: 'pointer', fontFamily: 'var(--font)' }}>CLR</button>
                        <button onClick={() => pinPress('0')} style={{ padding: '14px', fontSize: 18, fontWeight: 600, border: '1px solid var(--border)', borderRadius: 'var(--radius)', background: 'var(--bg)', color: 'var(--fg)', cursor: 'pointer', fontFamily: 'var(--font)' }}>0</button>
                        <button onClick={() => pinPress('back')} style={{ padding: '14px', fontSize: 16, border: '1px solid var(--border)', borderRadius: 'var(--radius)', background: 'var(--bg)', color: 'var(--fg2)', cursor: 'pointer', fontFamily: 'var(--font)' }}>⌫</button>
                      </div>
                    </div>
                    <input type="password" maxLength={4} inputMode="numeric" value={pin}
                      onChange={e => { const v = e.target.value.replace(/\D/g,'').slice(0,4); setPin(v); if (v.length===4) submitCredentials(v); }}
                      placeholder="Or type PIN" style={{ ...inputStyle, textAlign: 'center', letterSpacing: 10, fontSize: 18 }}
                      onFocus={e => e.target.style.borderColor = 'var(--accent)'}
                      onBlur={e => e.target.style.borderColor = 'var(--input-border)'}
                    />
                  </>
                )}
              </>
            )}

            {step === '2fa' && (
              <>
                <p style={{ fontSize: 13, color: 'var(--fg2)', marginBottom: 16 }}>Enter the 6-digit code from your authenticator app.</p>
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--fg2)', marginBottom: 6 }}>Code</label>
                  <input style={{ ...inputStyle, textAlign: 'center', letterSpacing: 10, fontSize: 20 }} type="text" inputMode="numeric" maxLength={6} value={totp}
                    onChange={e => setTotp(e.target.value.replace(/\D/g,'').slice(0,6))}
                    onKeyDown={e => e.key === 'Enter' && submit2FA()} autoFocus placeholder="000000" />
                </div>
                <button onClick={submit2FA} disabled={loading} style={{ width:'100%', padding:'10px', borderRadius:'var(--radius)', border:'none', background:'var(--accent)', color:'#fff', fontSize:14, fontWeight:600, cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1, fontFamily:'var(--font)', marginBottom: 10 }}>
                  {loading ? 'Verifying...' : 'Verify'}
                </button>
                <button onClick={() => { setStep('credentials'); setTotp(''); setError(''); }} style={{ background:'none', border:'none', color:'var(--fg3)', fontSize:12, cursor:'pointer', fontFamily:'var(--font)' }}>← Back</button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
